import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import { useConfig } from '../store/config';
import { sortReleasesByStart, colorForLane, type ReleaseCal, type ServicePack } from '../lib/releases';
import { useT, format } from '../i18n';

type Props = { open: boolean; onClose: () => void };

// Editier-Form für den Release-Kalender. Arbeitet auf einer lokalen Kopie,
// damit Schließen ohne Speichern verworfen wird. "Speichern" schreibt alles
// atomic via useConfig.setReleaseCal → POST /api/config.
export function ReleaseCalDialog({ open, onClose }: Props) {
  const releaseCal = useConfig(s => s.releaseCal);
  const setReleaseCal = useConfig(s => s.setReleaseCal);

  const [draft, setDraft] = useState<Record<string, ReleaseCal>>({});
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  // Beim Öffnen: Snapshot des aktuellen Stands in den Draft kopieren
  useEffect(() => {
    if (open) {
      setDraft(JSON.parse(JSON.stringify(releaseCal)));
      setNewName('');
      setError(null);
    }
  }, [open, releaseCal]);

  const releases = sortReleasesByStart(draft).length
    ? sortReleasesByStart(draft)
    : Object.keys(draft).sort();

  function patchRelease(name: string, patch: Partial<ReleaseCal>) {
    setDraft(d => ({ ...d, [name]: { ...d[name], ...patch } }));
  }

  function addSp(name: string) {
    setDraft(d => {
      const cal = d[name];
      const sps = [...(cal.sps ?? []), { name: `SP${(cal.sps?.length ?? 0) + 1}`, date: cal.end ?? '' }];
      return { ...d, [name]: { ...cal, sps } };
    });
  }

  function patchSp(name: string, idx: number, patch: Partial<ServicePack>) {
    setDraft(d => {
      const cal = d[name];
      const sps = (cal.sps ?? []).map((sp, i) => i === idx ? { ...sp, ...patch } : sp);
      return { ...d, [name]: { ...cal, sps } };
    });
  }

  function removeSp(name: string, idx: number) {
    setDraft(d => {
      const cal = d[name];
      const sps = (cal.sps ?? []).filter((_, i) => i !== idx);
      return { ...d, [name]: { ...cal, sps } };
    });
  }

  function addRelease() {
    const n = newName.trim();
    if (!n) return;
    if (draft[n]) { setError(`Release "${n}"`); return; }
    setDraft(d => ({ ...d, [n]: { start: '', end: '', sps: [] } }));
    setNewName('');
    setError(null);
  }

  function removeRelease(name: string) {
    if (!confirm(format(t.releaseCal.confirmDelete, { name }))) return;
    setDraft(d => {
      const next = { ...d };
      delete next[name];
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      // Validierung: alle Releases brauchen Start + Ende, Start ≤ Ende
      for (const [name, cal] of Object.entries(draft)) {
        if (!cal.start || !cal.end) { setError(format(t.releaseCal.errNeedDates, { name })); setBusy(false); return; }
        if (cal.start > cal.end)    { setError(format(t.releaseCal.errStartAfterEnd, { name, start: cal.start, end: cal.end })); setBusy(false); return; }
        for (const sp of cal.sps ?? []) {
          if (!sp.name || !sp.date) { setError(format(t.releaseCal.errSpMissing, { name })); setBusy(false); return; }
        }
      }
      await setReleaseCal(draft);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth slotProps={{ paper: { sx: { borderRadius: 4 } } }}>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CalendarMonthIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{t.releaseCal.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t.releaseCal.subtitle}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {!releases.length ? (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">{t.releaseCal.empty}</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {releases.map((name, i) => {
              const cal = draft[name];
              const color = colorForLane(i);
              return (
                <Paper
                  key={name}
                  variant="outlined"
                  sx={{ p: 2, borderLeft: `4px solid ${color}`, borderRadius: 2 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ color, fontWeight: 700, flex: 1 }}>
                      {name}
                    </Typography>
                    <Tooltip title={t.releaseCal.delRelease}>
                      <IconButton size="small" color="error" onClick={() => removeRelease(name)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      label={t.releaseCal.startLabel}
                      type="date"
                      size="small"
                      value={cal.start ?? ''}
                      onChange={(e) => patchRelease(name, { start: e.target.value })}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label={t.releaseCal.endLabel}
                      type="date"
                      size="small"
                      value={cal.end ?? ''}
                      onChange={(e) => patchRelease(name, { end: e.target.value })}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ flex: 1 }}
                    />
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, flex: 1 }}>
                      {format(t.releaseCal.servicePacks, { count: cal.sps?.length ?? 0 })}
                    </Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => addSp(name)}>
                      {t.releaseCal.addSp}
                    </Button>
                  </Stack>

                  <Stack spacing={1}>
                    {(cal.sps ?? []).map((sp, idx) => (
                      <Stack key={idx} direction="row" spacing={1.5} alignItems="center">
                        <TextField
                          label="Name"
                          size="small"
                          value={sp.name}
                          onChange={(e) => patchSp(name, idx, { name: e.target.value })}
                          sx={{ width: 100 }}
                        />
                        <TextField
                          label="Datum"
                          type="date"
                          size="small"
                          value={sp.date}
                          onChange={(e) => patchSp(name, idx, { date: e.target.value })}
                          slotProps={{ inputLabel: { shrink: true } }}
                          sx={{ width: 180 }}
                        />
                        <Tooltip title={t.releaseCal.removeSp}>
                          <IconButton size="small" onClick={() => removeSp(name, idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
          <TextField
            size="small"
            placeholder={t.releaseCal.releaseNamePh}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addRelease(); }}
            sx={{ width: 200 }}
          />
          <Button startIcon={<AddIcon />} onClick={addRelease}>{t.releaseCal.addRelease}</Button>
        </Stack>
        <Button onClick={onClose} disabled={busy}>{t.common.close}</Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {busy ? t.common.saving : t.common.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
